/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Formats a date string (YYYY-MM-DD) to the user's locale numeric format.
 *
 * @param dateString - An ISO date (`YYYY-MM-DD`) or a full ISO datetime (`YYYY-MM-DDTHH:mm:ss.sssZ`),
 *   of which only the date part is read
 * @param locale - The locale to use for formatting (e.g., 'en-GB', 'it-IT')
 * @param options - Which parts to render, defaulting to a full numeric date. Pass a narrower set for
 *   a surface that shows less — the PDP's restock date is drawn as `07/22`, month and day only.
 *   Field ORDER still follows the locale, so `en-GB` gives `22/07` from the same call.
 * @returns Formatted date string in the user's locale (e.g., 05/15/1990 for en-GB), or undefined if invalid
 *
 * @example
 * // Returns "05/15/1990" for en-GB
 * formatDateForLocale('1990-05-15', 'en-GB');
 *
 * @example
 * // Returns "15/05/1990" for it-IT
 * formatDateForLocale('1990-05-15', 'it-IT');
 *
 * @example
 * // Returns "07/22" for en-US
 * formatDateForLocale('2026-07-22T00:00:00.000Z', 'en-US', { month: '2-digit', day: '2-digit' });
 *
 * @example
 * // Returns undefined for invalid date
 * formatDateForLocale('invalid-date', 'en-GB');
 */
export function formatDateForLocale(
    dateString: string | undefined,
    locale: string,
    options: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' }
): string | undefined {
    if (!dateString) return undefined;
    try {
        // Accept a full ISO datetime as well as a bare date, by taking everything before the `T`.
        //
        // SCAPI supplies both shapes and the datetime is the common one — `inStockDate` arrives as
        // `2026-07-28T00:00:00.000Z`. Splitting on `-` alone made the third token `28T00:00:00.000Z`,
        // whose `Number()` is NaN, so every such value fell out through the guard below as
        // `undefined`. Silent, and it cost the PDP's backorder rows their restock date.
        const [datePart] = dateString.split('T');

        // Parse the date string (YYYY-MM-DD format)
        // We parse manually to avoid timezone issues with new Date('YYYY-MM-DD')
        // which interprets the date as UTC midnight and can shift to previous day in some timezones
        const [year, month, day] = datePart.split('-').map(Number);
        if (!year || !month || !day || isNaN(year) || isNaN(month) || isNaN(day)) {
            return undefined;
        }

        // Create date using local timezone (months are 0-indexed)
        const date = new Date(year, month - 1, day);

        // Validate the date components match (handles invalid dates like Feb 30)
        if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
            return undefined;
        }

        return date.toLocaleDateString(locale, options);
    } catch {
        return undefined;
    }
}
